/**
 * TipTap extension that assigns a stable UUID (`blockId`) to each top-level
 * document node. That id is the primary key of the `block` row in
 * PowerSync: it lets each block sync independently
 * (one block = one row).
 *
 * `appendTransaction` covers every path that creates nodes without an id or
 * with duplicate ids: Enter (split), paste, undo, drag & drop, etc.
 */
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { generateId } from "@fleet/utils";

/** StarterKit node types that can be top-level blocks. */
const TOP_LEVEL_BLOCK_TYPES = [
  "paragraph",
  "heading",
  "bulletList",
  "orderedList",
  "blockquote",
  "codeBlock",
  "horizontalRule",
] as const;

export const BlockId = Extension.create({
  name: "blockId",

  addGlobalAttributes() {
    return [
      {
        types: [...TOP_LEVEL_BLOCK_TYPES],
        attributes: {
          blockId: {
            default: null,
            // A split (Enter) creates a new node: it must receive its own id
            keepOnSplit: false,
            // The id lives only in the document JSON, never in the DOM
            rendered: false,
          },
        },
      },
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey("block-id-assign"),
        appendTransaction: (_transactions, _oldState, state) => {
          const { tr, doc } = state;
          let changed = false;
          const seen = new Set<string>();

          doc.forEach((node, offset) => {
            const id: unknown = node.attrs.blockId;
            if (typeof id !== "string" || id.length === 0 || seen.has(id)) {
              tr.setNodeMarkup(offset, undefined, { ...node.attrs, blockId: generateId() });
              changed = true;
              return;
            }
            seen.add(id);
          });

          return changed ? tr : null;
        },
      }),
    ];
  },
});
